import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Search, X } from 'lucide-react';
import NewsWatchStar from '@/components/NewsWatchStar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SLOT_COLORS = ['#0ea5e9', '#f43f5e', '#10b981', '#f59e0b'];
export const MAX_TICKERS = 4;

function TickerSearch({ disabled, exclude, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return undefined; }
    setBusy(true);
    const id = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/stocks/search`, { params: { q: q.trim(), limit: 8, dividend_filter: 'all' }, withCredentials: true });
        setResults((Array.isArray(r.data) ? r.data : []).filter((s) => !exclude.includes(s.ticker)));
      } catch { setResults([]); } finally { setBusy(false); }
    }, 200);
    return () => clearTimeout(id);
  }, [q, exclude]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={boxRef} className={`relative rounded-xl border-2 border-dashed border-border bg-muted/30 px-3 py-2.5 ${disabled ? 'opacity-50' : 'focus-within:ring-2 focus-within:ring-sky-400'}`} data-testid="true-return-search">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={q}
          disabled={disabled}
          placeholder={disabled ? `Maximum ${MAX_TICKERS} tickers — remove one to add another` : 'Add a ticker to compare (SCHD, VOO, JEPI…)'}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="true-return-search-input"
        />
        {busy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>
      {open && q.trim() && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-72 overflow-y-auto rounded-lg border bg-popover shadow-lg" data-testid="true-return-search-results">
          {results.length === 0 && !busy && <div className="px-3 py-3 text-xs text-muted-foreground">No matches.</div>}
          {results.map((s) => (
            <button
              key={s.ticker}
              type="button"
              onClick={() => { setOpen(false); setQ(''); onPick(s.ticker); }}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted transition-colors border-b border-border/40 last:border-b-0"
              data-testid={`true-return-search-result-${s.ticker}`}
            >
              <span className="text-sm font-bold font-mono">{s.ticker}</span>
              <span className="truncate text-[11px] text-muted-foreground flex-1">{s.name}</span>
              <span className="shrink-0 text-xs font-mono text-emerald-600 dark:text-emerald-400">{s.dividend_yield ? `${Number(s.dividend_yield).toFixed(2)}%` : '—'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TickerRow({ ticker, idx, meta, dripOn, onToggleDrip, onRemove, onOpenStock }) {
  const color = SLOT_COLORS[idx];
  return (
    <div className="rounded-xl border-2 bg-card px-2.5 sm:px-3 py-2" style={{ borderColor: color }} data-testid={`true-return-row-${ticker}`}>
    <div className="flex items-center gap-2 sm:gap-3">
      <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: color }} />
      <NewsWatchStar ticker={ticker} isDividend={meta?.is_dividend !== false} variant="table" />
      <button
        type="button"
        onClick={() => onOpenStock(ticker)}
        className="font-mono font-bold text-base sm:text-lg hover:underline underline-offset-2"
        style={{ color }}
        title="Open full stock data"
        data-testid={`true-return-ticker-${ticker}`}
      >
        {ticker}
      </button>
      <label className="ml-auto flex items-center gap-1.5 text-xs sm:text-sm cursor-pointer select-none shrink-0" data-testid={`true-return-drip-label-${ticker}`}>
        <input
          type="checkbox"
          checked={dripOn}
          onChange={() => onToggleDrip(ticker)}
          className="h-4 w-4 accent-emerald-600"
          data-testid={`true-return-drip-${ticker}`}
        />
        Drip On
      </label>
      <button type="button" onClick={() => onRemove(ticker)} className="text-muted-foreground hover:text-foreground p-1" aria-label={`Remove ${ticker}`} data-testid={`true-return-remove-${ticker}`}>
        <X className="w-4 h-4" />
      </button>
    </div>
    {meta?.name && (
      <p className="mt-1 truncate text-xs text-muted-foreground" title={meta.name} data-testid={`true-return-name-${ticker}`}>{meta.name}</p>
    )}
    </div>
  );
}

export default function TrueReturnPicker({ tickers, series, dripOn, onAdd, onRemove, onToggleDrip, onOpenStock }) {
  return (
    <div className="space-y-2" data-testid="true-return-picker">
      {tickers.map((t, i) => (
        <TickerRow key={t} ticker={t} idx={i} meta={series?.[t]} dripOn={dripOn[t] !== false} onToggleDrip={onToggleDrip} onRemove={onRemove} onOpenStock={onOpenStock} />
      ))}
      <TickerSearch disabled={tickers.length >= MAX_TICKERS} exclude={tickers} onPick={onAdd} />
      {tickers.length < 2 && (
        <p className="text-xs text-muted-foreground" data-testid="true-return-need-two">Pick at least 2 tickers (up to {MAX_TICKERS}) to draw the comparison.</p>
      )}
    </div>
  );
}
